/**
 * Several steps at once in the flow editor (1.9.3): the selection is a list of step ids, the last one clicked being the
 * primary (a step added from the palette connects after it). Pure helpers, unit-tested in
 * apps/web/test/flow-builder-193.test.ts; GraphCanvas and Builder use them. Select, move and delete only (no copy and
 * paste, no undo).
 */
import type { AutomationGraph, AutomationNode } from '@prism/contracts';
import { boxOf, isTinyBox, type Box } from '../create/selection';

export { boxOf, isTinyBox, type Box };

/** A step's drawn size on the canvas, in canvas pixels (before the zoom). */
export const NODE_W = 220;
export const NODE_H = 72;
/** The grid steps snap to. */
export const GRID = 10;

export type GraphSelection = readonly string[];

/** The primary step: the last one clicked. */
export const primaryOf = (sel: GraphSelection): string | null => sel[sel.length - 1] ?? null;

/** A click on a step: it alone, or with Shift or ⌘ (`extend`) added to the others or removed from them. */
export function clickNode(sel: GraphSelection, id: string, extend: boolean): string[] {
  if (!extend) return [id];
  return sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id];
}

/**
 * Pressing the pointer on a step, which may become a click or a drag (the timeline's pressItem rule):
 *  - `now`: the selection at once, which is also what a drag moves;
 *  - `click`: the selection when the pointer comes up without moving (null: stays `now`).
 * A step that is already in a multi-selection keeps the group for a drag, and a click then selects it alone (with
 * Shift or ⌘ it removes it). An unselected step is selected at once (with Shift or ⌘ added to the others).
 */
export function pressNode(sel: GraphSelection, id: string, extend: boolean): { now: string[]; click: string[] | null } {
  const selected = sel.includes(id);
  const others = sel.filter((x) => x !== id);
  if (extend) return selected ? { now: [...others, id], click: others } : { now: [...sel, id], click: null };
  if (selected) return { now: [...others, id], click: sel.length === 1 ? null : [id] };
  return { now: [id], click: null };
}

/** The steps a rubber band touches (touching an edge counts), in the graph's order. */
export function nodesInBox(nodes: ReadonlyArray<Pick<AutomationNode, 'id' | 'position'>>, box: Box, w = NODE_W, h = NODE_H): string[] {
  return nodes.filter((n) => n.position.x <= box.right && n.position.x + w >= box.left && n.position.y <= box.bottom && n.position.y + h >= box.top).map((n) => n.id);
}

/** The band's result: the steps in it, or with Shift or ⌘ added to what was selected. */
export function bandSelection(sel: GraphSelection, inBox: readonly string[], extend: boolean): string[] {
  if (!extend) return [...inBox];
  return [...sel, ...inBox.filter((id) => !sel.includes(id))];
}

/** Every step (⌘A), the trigger first as in the graph. */
export const selectAll = (graph: AutomationGraph): string[] => graph.nodes.map((n) => n.id);

/**
 * The one delta a drag moves the steps by: snapped so that the dragged step (`anchor`, else the first moved) lands on
 * the 10 px grid, and clamped so that no moved step goes above or left of 0.
 */
export function moveDelta(graph: AutomationGraph, ids: readonly string[], dx: number, dy: number, anchor?: string): { dx: number; dy: number } {
  const moved = graph.nodes.filter((n) => ids.includes(n.id));
  if (!moved.length) return { dx: 0, dy: 0 };
  const a = moved.find((n) => n.id === anchor) ?? moved[0]!;
  let sx = Math.round((a.position.x + dx) / GRID) * GRID - a.position.x;
  let sy = Math.round((a.position.y + dy) / GRID) * GRID - a.position.y;
  sx = Math.max(sx, -Math.min(...moved.map((n) => n.position.x)));
  sy = Math.max(sy, -Math.min(...moved.map((n) => n.position.y)));
  return { dx: sx + 0, dy: sy + 0 };
}

/** The graph with the steps moved together by one snapped and clamped delta (moveDelta); unchanged when it is 0. */
export function moveNodes(graph: AutomationGraph, ids: readonly string[], dx: number, dy: number, anchor?: string): AutomationGraph {
  const d = moveDelta(graph, ids, dx, dy, anchor);
  if (!d.dx && !d.dy) return graph;
  const set = new Set(ids);
  return { ...graph, nodes: graph.nodes.map((n) => (set.has(n.id) ? { ...n, position: { x: n.position.x + d.dx, y: n.position.y + d.dy } } : n)) };
}

/** The steps of these that can be removed: every one but the trigger. */
export function removable(graph: AutomationGraph, ids: readonly string[]): string[] {
  return graph.nodes.filter((n) => ids.includes(n.id) && n.type !== 'trigger').map((n) => n.id);
}

/** The graph without these steps (never the trigger) and their lines, keeping its schemaVersion; unchanged when none goes. */
export function removeNodes(graph: AutomationGraph, ids: readonly string[]): AutomationGraph {
  const gone = new Set(removable(graph, ids));
  if (!gone.size) return graph;
  return { ...graph, nodes: graph.nodes.filter((n) => !gone.has(n.id)), edges: graph.edges.filter((e) => !gone.has(e.from) && !gone.has(e.to)) };
}
